import { useState } from 'react'
import { AlertTriangle, Trash2, Database, Cloud, HardDrive } from 'lucide-react'
import Modal, { FormField, FormActions, Button } from '../shared/Modal'
import { getAPIBaseUrl } from '../../environment'
import './DeleteAccountConfirmation.css'

const API_BASE_URL = getAPIBaseUrl()

function DeleteAccountConfirmation({ account, isOpen, onClose, onConfirm }) {
  const [deleteStorage, setDeleteStorage] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  if (!account) return null

  const isS3 = account.storageType === 's3'
  const isLocal = account.storageType === 'local'

  const handleDelete = async () => {
    setDeleting(true)
    setError(null)

    try {
      // If S3 storage and user wants to delete bucket/files
      if (isS3 && deleteStorage) {
        console.log(`[Delete] Deleting S3 bucket: ${account.s3Bucket}`)

        const response = await fetch(`${API_BASE_URL}/s3/delete-bucket`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bucket_name: account.s3Bucket,
            force: true // Force delete all objects
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Failed to delete S3 bucket')
        }

        const result = await response.json()
        console.log(`[Delete] S3 bucket deleted: ${result.message}`)
      }

      // If local storage and user wants to delete files
      if (isLocal && deleteStorage) {
        console.log(`[Delete] Local storage deletion not yet implemented for: ${account.localPath || './data/' + account.scoutAccountId}`)
        // TODO: Add local file deletion via API when implemented
      }

      // Delete account from database
      await onConfirm()

      onClose()
    } catch (err) {
      console.error('[Delete] Failed to delete account:', err)
      setError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleCancel = () => {
    setDeleteStorage(false)
    setError(null)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Delete Account"
      size="medium"
    >
      <div className="delete-confirmation">
        <div className="warning-header">
          <AlertTriangle size={48} className="warning-icon" />
          <h3>Are you sure you want to delete this account?</h3>
        </div>

        <div className="account-details">
          <div className="detail-row">
            <strong>Account ID:</strong>
            <code>{account.scoutAccountId}</code>
          </div>
          <div className="detail-row">
            <strong>Account Name:</strong>
            <span>{account.name}</span>
          </div>
          <div className="detail-row">
            <strong>Storage Type:</strong>
            <span className="storage-badge">
              {isS3 ? <Cloud size={14} /> : <HardDrive size={14} />}
              {isS3 ? 'AWS S3' : 'Local Filesystem'}
            </span>
          </div>
        </div>

        {/* S3 Storage Warning */}
        {isS3 && (
          <div className="storage-warning s3-warning">
            <div className="warning-title">
              <Cloud size={20} />
              <strong>S3 Bucket Data</strong>
            </div>
            <p>This account uses S3 storage:</p>
            <code className="storage-path">s3://{account.s3Bucket}/{account.scoutAccountId}/</code>

            <label className="delete-storage-option">
              <input
                type="checkbox"
                checked={deleteStorage}
                onChange={(e) => setDeleteStorage(e.target.checked)}
              />
              <div className="option-content">
                <strong>Also delete S3 bucket and all data</strong>
                <p className="option-warning">
                  This will permanently delete the S3 bucket "{account.s3Bucket}" and ALL files in it.
                  This action cannot be undone!
                </p>
              </div>
            </label>

            {!deleteStorage && (
              <div className="info-note">
                <Database size={16} />
                <span>S3 bucket and files will be kept. You can manage them in AWS Console.</span>
              </div>
            )}
          </div>
        )}

        {/* Local Storage Warning */}
        {isLocal && (
          <div className="storage-warning local-warning">
            <div className="warning-title">
              <HardDrive size={20} />
              <strong>Local Storage Data</strong>
            </div>
            <p>This account uses local storage:</p>
            <code className="storage-path">
              {account.localPath || `./data/${account.scoutAccountId}/`}
            </code>

            <label className="delete-storage-option">
              <input
                type="checkbox"
                checked={deleteStorage}
                onChange={(e) => setDeleteStorage(e.target.checked)}
              />
              <div className="option-content">
                <strong>Also delete local files and folders</strong>
                <p className="option-warning">
                  This will permanently delete all files in this directory.
                  This action cannot be undone!
                </p>
              </div>
            </label>

            {!deleteStorage && (
              <div className="info-note">
                <Database size={16} />
                <span>Local files will be kept. You can manually delete them if needed.</span>
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <AlertTriangle size={20} />
            <div>
              <strong>Deletion Failed</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Final Warning */}
        <div className="final-warning">
          <Trash2 size={20} />
          <div>
            <strong>This will delete:</strong>
            <ul>
              <li>Account record from database</li>
              {deleteStorage && isS3 && <li>S3 bucket "{account.s3Bucket}" and all objects</li>}
              {deleteStorage && isLocal && <li>All local files in {account.localPath || `./data/${account.scoutAccountId}/`}</li>}
              {!deleteStorage && <li className="kept">Storage data will be kept</li>}
            </ul>
          </div>
        </div>

        <FormActions>
          <Button variant="secondary" onClick={handleCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : deleteStorage ? 'Delete Account & Storage' : 'Delete Account Only'}
          </Button>
        </FormActions>
      </div>
    </Modal>
  )
}

export default DeleteAccountConfirmation
